import { useAction, useMutation, useQuery } from "convex/react";
import { useState } from "react";
import { Link } from "react-router";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { api } from "../../../../convex/_generated/api";
import type { Id } from "../../../../convex/_generated/dataModel";
import type { MailAccount } from "./types";

type FilterList = {
	fromBlocklist: string[];
	fromAllowlist: string[];
	toMatch: string[];
	subjectMustContain: string[];
	subjectMustNotContain: string[];
};

type FilterConfig = {
	connectionId: Id<"oauthConnections">;
	enabled: boolean;
	syncWindowDays: number;
	filters: FilterList;
	backfillStatus?: string;
	backfillProcessed?: number;
	backfillLastError?: string;
};

const FILTER_KEYS: Array<{
	key: keyof FilterList;
	label: string;
	placeholder: string;
	help: string;
}> = [
	{
		key: "fromBlocklist",
		label: "From — block",
		placeholder: "spam@example.com, @newsletter.co",
		help: "Emails matching any entry will be ignored (not ingested).",
	},
	{
		key: "fromAllowlist",
		label: "From — only allow",
		placeholder: "@trusted.com",
		help: "If any entries are set, only emails from these will be ingested.",
	},
	{
		key: "toMatch",
		label: "To must match",
		placeholder: "support@, partner@yourfirm.com",
		help: "If set, at least one recipient must match.",
	},
	{
		key: "subjectMustContain",
		label: "Subject must contain",
		placeholder: "invoice, wire transfer",
		help: "If set, subject must contain at least one phrase.",
	},
	{
		key: "subjectMustNotContain",
		label: "Subject must not contain",
		placeholder: "unsubscribe, marketing",
		help: "Emails whose subject contains any of these will be ignored.",
	},
];

function parseList(raw: string): string[] {
	return raw
		.split(/[\n,]/)
		.map((s) => s.trim())
		.filter(Boolean);
}

function formatList(list: string[]): string {
	return list.join("\n");
}

export default function MailSettingsPage() {
	const accountsQuery = useQuery(api.mail.queries.listAccounts);
	const isLoadingAccounts = accountsQuery === undefined;
	const accounts: MailAccount[] = accountsQuery ?? [];

	return (
		<ScrollArea className="h-full">
			<div className="mx-auto flex w-full max-w-3xl flex-col gap-6 p-6">
				<div className="flex items-center justify-between">
					<div>
						<h1 className="text-2xl font-semibold">Mail settings</h1>
						<p className="text-sm text-muted-foreground">
							Configure per-mailbox filters and sync behavior.
						</p>
					</div>
					<Link
						to="/staff/mail"
						className="text-sm text-muted-foreground underline"
					>
						Back to mail
					</Link>
				</div>
				<Separator />
				{isLoadingAccounts ? (
					<div className="text-sm text-muted-foreground">Loading…</div>
				) : accounts.length === 0 ? (
					<div className="text-sm text-muted-foreground">
						No connected mailboxes in this tenant.
					</div>
				) : (
					<div className="flex flex-col gap-6">
						{accounts.map((acct) => (
							<AccountSettingsCard key={acct.connectionId} account={acct} />
						))}
					</div>
				)}
			</div>
		</ScrollArea>
	);
}

function AccountSettingsCard({ account }: { account: MailAccount }) {
	const config = useQuery(api.mail.queries.getFilterConfig, {
		connectionId: account.connectionId,
	});

	if (config === undefined) {
		return (
			<div className="rounded-lg border p-4 text-sm text-muted-foreground">
				Loading {account.accountEmail}…
			</div>
		);
	}

	if (config === null) {
		return (
			<div className="rounded-lg border p-4 text-sm text-muted-foreground">
				No settings for {account.accountEmail}.
			</div>
		);
	}

	return (
		<AccountSettingsForm
			key={account.connectionId}
			account={account}
			initialConfig={config}
		/>
	);
}

function AccountSettingsForm({
	account,
	initialConfig,
}: {
	account: MailAccount;
	initialConfig: FilterConfig;
}) {
	const setFilterConfig = useMutation(api.mail.mutations.setFilterConfig);
	const triggerSync = useAction(api.mail.triggerSync.triggerFullSync);

	const [enabled, setEnabled] = useState<boolean>(initialConfig.enabled);
	const [windowDays, setWindowDays] = useState<number>(
		initialConfig.syncWindowDays,
	);
	const [filters, setFilters] = useState<FilterList>({
		fromBlocklist: initialConfig.filters.fromBlocklist,
		fromAllowlist: initialConfig.filters.fromAllowlist,
		toMatch: initialConfig.filters.toMatch,
		subjectMustContain: initialConfig.filters.subjectMustContain,
		subjectMustNotContain: initialConfig.filters.subjectMustNotContain,
	});
	const [saving, setSaving] = useState(false);
	const [syncing, setSyncing] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const save = async () => {
		setError(null);
		setSaving(true);
		try {
			await setFilterConfig({
				connectionId: account.connectionId,
				enabled,
				syncWindowDays: windowDays,
				filters,
			});
		} catch (err) {
			setError(err instanceof Error ? err.message : String(err));
		} finally {
			setSaving(false);
		}
	};

	const runSync = async () => {
		setError(null);
		setSyncing(true);
		try {
			await triggerSync({
				connectionId: account.connectionId,
				windowDays,
			});
		} catch (err) {
			setError(err instanceof Error ? err.message : String(err));
		} finally {
			setSyncing(false);
		}
	};

	return (
		<div className="rounded-lg border p-4">
			<div className="flex items-start justify-between gap-4">
				<div>
					<div className="font-semibold">{account.accountEmail}</div>
					<div className="text-xs text-muted-foreground">
						{account.provider === "gmail" ? "Gmail" : "Outlook"} ·{" "}
						{account.ownerName}
					</div>
					{initialConfig.backfillStatus && (
						<div className="mt-1 text-xs text-muted-foreground">
							Backfill status:{" "}
							<span className="font-medium">
								{initialConfig.backfillStatus}
							</span>
							{typeof initialConfig.backfillProcessed === "number" && (
								<span> ({initialConfig.backfillProcessed} ingested)</span>
							)}
							{initialConfig.backfillLastError && (
								<span className="ml-2 text-destructive">
									{initialConfig.backfillLastError}
								</span>
							)}
						</div>
					)}
				</div>
				<div className="flex items-center gap-2">
					<Label
						htmlFor={`enabled-${account.connectionId}`}
						className="flex items-center gap-2 text-xs"
					>
						<Switch
							id={`enabled-${account.connectionId}`}
							checked={enabled}
							onCheckedChange={setEnabled}
						/>
						{enabled ? "Ingestion on" : "Ingestion off"}
					</Label>
				</div>
			</div>
			<Separator className="my-4" />
			<div className="grid gap-4">
				<div className="flex items-center gap-2">
					<Label htmlFor={`window-${account.connectionId}`} className="text-xs">
						Sync window (days)
					</Label>
					<Input
						id={`window-${account.connectionId}`}
						type="number"
						min={1}
						max={365}
						value={windowDays}
						onChange={(e) => setWindowDays(Number(e.target.value) || 14)}
						className="w-24"
					/>
				</div>
				{FILTER_KEYS.map((f) => (
					<div key={f.key} className="grid gap-1">
						<Label className="text-xs">{f.label}</Label>
						<textarea
							className="min-h-[60px] w-full rounded border bg-background p-2 text-xs"
							placeholder={f.placeholder}
							value={formatList(filters[f.key])}
							onChange={(e) =>
								setFilters((prev) => ({
									...prev,
									[f.key]: parseList(e.target.value),
								}))
							}
						/>
						<p className="text-xs text-muted-foreground">{f.help}</p>
					</div>
				))}
				{error && <div className="text-xs text-destructive">{error}</div>}
				<div className="flex items-center gap-2">
					<Button onClick={save} disabled={saving}>
						{saving ? "Saving…" : "Save filters"}
					</Button>
					<Button variant="outline" onClick={runSync} disabled={syncing}>
						{syncing ? "Kicking off…" : "Re-run sync"}
					</Button>
				</div>
			</div>
		</div>
	);
}
